import { BrowserRouter, Routes, Route, useNavigate } from 'react-router'
import { get, isEqual } from 'es-toolkit/compat'
import { Box } from '@mui/material'
import Header from 'containers/header'
import { ContentWrapper } from 'components/page'
import { closeModal, closePopup } from 'actions/app'
import Modal from 'components/modal'
import Popup from 'components/popup'
import 'ol/ol.css'
import MapPanel from 'containers/map-panel'
import * as c from 'common/c'

import FarmSettings from 'components/farm-settings'
import { AdminComponent } from 'components/admin'
import CffComponent from 'components/cff'
import { Orders } from 'components/orders'

import Support from 'components/support'
import SplitMapPanel from 'containers/split-map-panel'
import Reports from 'components/reports'
import FeatureFlagProvider from 'components/feature-flag-provider'
import { TEST_IDS } from 'test-ids'
import { updateLayer, setPreviewLegend } from 'actions/map'

export default function AppInternal() {
  const dispatch = useDispatch()

  const { i18n } = useTranslation()
  const customClaims = useSelector((state) => state.account.customClaims)
  const permissions = useSelector((state) => state.account.permissions)
  const showPermissions = {
    admin:
      permissions.includes('users:write') ||
      permissions.includes('tenant:list'),
    reports: permissions.includes('reports:read'),
    orders: permissions.includes('order:write'),
  }
  const language = useSelector(
    (state) => state.account.singleton.prefs?.general?.language,
  )

  //Set the user selected language if any
  useEffect(() => {
    if (language) {
      const isAdmin =
        permissions.includes('farmtrx:admin') ||
        customClaims?.[c.FT_ADMIN_CLAIM]
      if (!isAdmin) {
        i18n.changeLanguage(language)
      }
    }
  }, [language, permissions, customClaims])

  // Modal window
  const modal = useSelector((state) => get(state, 'app.modal'))
  const modalProps = get(modal, 'props', {})
  const modalComponent = get(modal, 'component')

  // Popup window
  const popup = useSelector((state) => get(state, 'app.popup'))
  const popupComponent = get(popup, 'component')
  const popupProperties = get(popup, 'props')

  const view = useSelector((state) => get(state, 'app.view'))

  const previewLegend = useSelector((state) => get(state, 'map.previewLegend'))
  const layerInfo = useSelector((state) => get(state, 'field.layerInfo'))
  const wmsLayers = useSelector((state) => get(state, 'map.wmsLayers'))

  const onModalClose = useCallback(() => {
    dispatch(closeModal())
    if (previewLegend && layerInfo) {
      //Compare the WMS Layer variable with the layerInfo variable. If they are the same, then we can close the modal and no further action is needed.
      // Else, reset the WMS Layer to the Layer Info variable layer legend.
      const layer = layerInfo.find((layer) => {
        return (
          wmsLayers[0].name === layer.layerName &&
          wmsLayers[0].harvestId === layer.harvestId
        )
      })
      if (layer) {
        if (!isEqual(wmsLayers[0].customLegends, layer.customLegends)) {
          const updatedLayer = {
            ...layer,
            customLegends: layer.customLegends,
          }
          dispatch(updateLayer(updatedLayer))
        }
      }
    }
    dispatch(setPreviewLegend(false))
  }, [previewLegend, layerInfo, wmsLayers])

  const onPopupClose = useCallback(() => {
    dispatch(closePopup())
  }, [popup])

  function Redirect({ to }) {
    const navigate = useNavigate()
    useEffect(() => {
      console.log('Redirecting to:', to)
      navigate(to)
    })
    return null
  }

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
      }}
    >
      <FeatureFlagProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <Header />
          <ContentWrapper
            id={TEST_IDS.appContentArea}
            data-testid={TEST_IDS.appContentArea}
            style={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}
          >
            <Routes>
              {showPermissions.reports && (
                <Route path="/reports" element={<Reports />} />
              )}
              {showPermissions.orders && (
                <Route path="/orders" element={<Orders />} />
              )}
              {showPermissions.admin && (
                <Route path="/admin">
                  <Route index path="*" element={<AdminComponent />} />
                </Route>
              )}
              <Route path="/support" element={<Support />} />
              <Route path="/farmSetup">
                <Route index path="*" element={<FarmSettings />} />
              </Route>
              <Route path="/fields">
                <Route index path="*" element={<CffComponent />} />
              </Route>

              {localStorage.getItem('wizardActiveStep') == 'null' ? (
                <Route path="/wizardStepper" element={<Redirect to="/" />} />
              ) : null}
              <Route
                path="/"
                element={view === 'split' ? <SplitMapPanel /> : <MapPanel />}
              ></Route>
              {/* <Toaster /> */}
            </Routes>
          </ContentWrapper>
        </Box>
        {modal ? (
          <Modal
            component={modalComponent}
            onClose={onModalClose}
            title={modalProps.title}
            draggable={modalProps.draggable}
            maxWidth={modalProps.maxWidth}
            fullWidth={modalProps.fullWidth}
          />
        ) : null}
        {popup ? (
          <Popup
            component={popupComponent}
            onClose={onPopupClose}
            title={popupProperties.title}
            pixel={{
              x: popupProperties.pixel[0],
              y: popupProperties.pixel[1],
            }}
          />
        ) : null}
      </FeatureFlagProvider>
    </BrowserRouter>
  )
}
